import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Table, Modal, Form, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import { FaPlus, FaSearch, FaEye, FaPlay, FaCheck, FaTimes, FaTicketAlt, FaInfoCircle, FaImage, FaFile, FaDownload, FaPaperPlane, FaPaperclip, FaComments } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import apiClient from '../../../api/apiClient';

const OrgSupportTicketsPage = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({});
  const [activeTab, setActiveTab] = useState('user-tickets');
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  // Form states
  const [createForm, setCreateForm] = useState({
    subject: '',
    description: '',
    is_organization_ticket: true
  });

  const [messageForm, setMessageForm] = useState({
    message: '',
    is_internal: false
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const statusColors = {
    open: 'primary',
    in_progress: 'info',
    resolved: 'success',
    closed: 'secondary'
  };

  useEffect(() => {
    fetchTickets();
  }, [filters, activeTab]);

  useEffect(() => {
    if (tickets.length >= 0) {
      fetchStats();
    }
  }, [tickets]);

  const fetchTickets = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...filters
      });

      const response = await apiClient.get(`/support-tickets?${params}`);
      
      if (response.data.success) {
        setTickets(response.data.data.tickets);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/support-tickets/admin/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      // If admin stats fail, calculate basic stats from tickets
      const basicStats = {
        total: tickets.length,
        open: tickets.filter(t => t.status === 'open').length,
        in_progress: tickets.filter(t => t.status === 'in_progress').length,
        resolved: tickets.filter(t => t.status === 'resolved').length,
        closed: tickets.filter(t => t.status === 'closed').length
      };
      setStats(basicStats);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiClient.post('/support-tickets', createForm);
      
      if (response.data.success) {
        setSuccess('Organization ticket created successfully');
        setShowCreateModal(false);
        setCreateForm({
          subject: '',
          description: '',
          is_organization_ticket: true
        });
        fetchTickets();
        fetchStats();
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      setError(error.response?.data?.message || 'Failed to create support ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/support-tickets/${ticketId}`);
      
      if (response.data.success) {
        setSelectedTicket(response.data.data);
        setShowTicketModal(true);
        setMessageForm({ message: '', is_internal: false });
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setError('Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      const response = await apiClient.patch(`/support-tickets/${ticketId}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        setSuccess(`Ticket status updated to ${newStatus.replace('_', ' ')}`);
        fetchTickets();
        fetchStats();
        if (selectedTicket && selectedTicket.id === ticketId) {
          setSelectedTicket(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      setError(error.response?.data?.message || 'Failed to update ticket status');
    }
  };

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      await apiClient.supportTickets.downloadAttachment(attachmentId);
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download attachment. Please try again.');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.message.trim() && !selectedFile) return;

    try {
      setLoading(true);
      
      if (messageForm.message.trim()) {
        const messageResponse = await apiClient.post(`/support-tickets/${selectedTicket.id}/messages`, {
          message: messageForm.message.trim(),
          is_internal: messageForm.is_internal
        });
        
        if (!messageResponse.data.success) {
          throw new Error('Failed to send message');
        }
      }

      if (selectedFile) {
        const formData = new FormData();
        formData.append('attachment', selectedFile);
        
        const attachmentResponse = await apiClient.post(`/support-tickets/${selectedTicket.id}/attachments`, formData);
        
        if (!attachmentResponse.data.success) {
          throw new Error('Failed to upload attachment');
        }
      }

      const refreshResponse = await apiClient.get(`/support-tickets/${selectedTicket.id}`);
      if (refreshResponse.data.success) {
        setSelectedTicket(refreshResponse.data.data);
      }

      setMessageForm({ message: '', is_internal: false });
      setSelectedFile(null);
      setSuccess('Message sent successfully');

    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.response?.data?.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredTickets = () => {
    if (activeTab === 'user-tickets') {
      return tickets.filter(ticket => !ticket.is_organization_ticket);
    } else {
      return tickets.filter(ticket => ticket.is_organization_ticket);
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="mb-4">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Support Tickets
        </h5>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-3">
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Row className="mb-4 g-3">
        <Col xs={6} sm={4} md={2}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{stats.total || 0}</h3>
              <small className="text-white opacity-90">Total Tickets</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={4} md={2}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{stats.open || 0}</h3>
              <small className="text-white opacity-90">Open</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={4} md={2}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{stats.in_progress || 0}</h3>
              <small className="text-white opacity-90">In Progress</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={4} md={2}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-green-500 to-emerald-500 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{stats.resolved || 0}</h3>
              <small className="text-white opacity-90">Resolved</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={4} md={2}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-gray-500 to-gray-600 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{stats.closed || 0}</h3>
              <small className="text-white opacity-90">Closed</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-4 g-2 sm:g-3">
        <Col xs={12} sm={6} md={3} className="mb-2 mb-md-0">
          <Form.Select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={6} className="mb-2 mb-md-0">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search by subject, description, or ticket number..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => setShowCreateModal(true)}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaPlus className="w-4 h-4" />
            <span className="whitespace-nowrap">Create Ticket</span>
          </Button>
        </Col>
      </Row>

      {/* Tickets Tabs */}
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <Card.Body className="p-0">
          <div className="bg-white p-3 border-b border-purple-100">
            <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-0">
              <Tab eventKey="user-tickets" title="User Tickets" className="text-sm">
                <p className="text-muted mb-0 text-xs sm:text-sm mt-2">
                  Tickets created by users in your organization
                </p>
              </Tab>
              <Tab eventKey="org-tickets" title="Organization Tickets" className="text-sm">
                <p className="text-muted mb-0 text-xs sm:text-sm mt-2">
                  Organization-level tickets created by admins
                </p>
              </Tab>
            </Tabs>
          </div>

          {loading ? (
            <div className="text-center py-10 bg-white">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto"></div>
              <p className="mt-3 text-gray-600">Loading tickets...</p>
            </div>
          ) : getFilteredTickets().length === 0 ? (
            <div className="items-center justify-center text-center flex flex-col py-10 bg-white">
              <div className="flex justify-center items-center w-full">
                <FaTicketAlt className="text-gray-300 mb-3" style={{ fontSize: '3rem' }} />
              </div>
              <h5 className="text-gray-600 mb-2">No tickets found</h5>
              <p className="text-muted text-sm">
                {activeTab === 'user-tickets'
                  ? 'No user tickets found for your organization.'
                  : 'No organization tickets found. Create one to escalate issues to Super Admin.'
                }
              </p>
            </div>
       
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3">Ticket #</th>
                    <th className="py-2 px-3">Subject</th>
                    <th className="py-2 px-3">Created By</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Created</th>
                    <th className="py-2 px-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredTickets().map((ticket) => (
                    <tr key={ticket.id} className="border-b border-purple-50 hover:bg-purple-50/60 transition-all">
                      <td className="py-2 px-3 align-middle">
                        <code className="text-purple-600 fw-bold">{ticket.ticket_number}</code>
                        {ticket.is_organization_ticket && (
                          <Badge bg="info" className="ms-1">ORG</Badge>
                        )}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="fw-semibold text-gray-800 text-sm">{ticket.subject}</div>
                        <small className="text-muted text-xs">
                          {ticket.description.substring(0, 60)}
                          {ticket.description.length > 60 && '...'}
                        </small>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="text-gray-800 text-sm">{ticket.creator.first_name} {ticket.creator.last_name}</div>
                        <small className="text-muted text-xs">{ticket.creator.email}</small>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg={statusColors[ticket.status]} className="text-capitalize">
                          {ticket.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-xs">
                        {formatDate(ticket.createdAt)}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            onClick={() => handleViewTicket(ticket.id)}
                            className="text-purple-600 hover:bg-purple-100 p-2 rounded-lg transition-all"
                            title="View Ticket"
                          >
                            <FaEye className="w-5 h-5" />
                          </button>
                          
                          {ticket.status === 'open' && (
                            <button
                              onClick={() => handleUpdateStatus(ticket.id, 'in_progress')}
                              className="text-cyan-600 hover:bg-cyan-100 p-2 rounded-lg transition-all"
                              title="Mark as In Progress"
                            >
                              <FaPlay className="w-5 h-5" />
                            </button>
                          )}
                          
                          {ticket.status === 'in_progress' && (
                            <button
                              onClick={() => handleUpdateStatus(ticket.id, 'resolved')}
                              className="text-emerald-600 hover:bg-emerald-100 p-2 rounded-lg transition-all"
                              title="Mark as Resolved"
                            >
                              <FaCheck className="w-5 h-5" />
                            </button>
                          )}
                          
                          {ticket.status === 'resolved' && (
                            <button
                              onClick={() => handleUpdateStatus(ticket.id, 'closed')}
                              className="text-gray-600 hover:bg-gray-200 p-2 rounded-lg transition-all"
                              title="Close Ticket"
                            >
                              <FaTimes className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4 mb-3 bg-white py-3">
              <Button
                bsPrefix="custom-hamburger"
                disabled={pagination.page === 1}
                onClick={() => fetchTickets(pagination.page - 1)}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm px-4 py-2 me-2"
              >
                Previous
              </Button>
              <span className="align-self-center mx-3 text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                bsPrefix="custom-hamburger"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => fetchTickets(pagination.page + 1)}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm px-4 py-2"
              >
                Next
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create Organization Ticket Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Create Organization Ticket
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateTicket}>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-blue-800">
                <FaInfoCircle className="w-5 h-5" />
                <span className="text-sm font-semibold">Organization tickets are escalated directly to Super Admin for organization-level issues.</span>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Subject <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="Brief description of the organization issue"
                value={createForm.subject}
                onChange={(e) => setCreateForm(prev => ({ ...prev, subject: e.target.value }))}
                required
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
              <textarea
                rows={5}
                placeholder="Please provide detailed information about the organization issue..."
                value={createForm.description}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                required
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
            </div>

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={() => setShowCreateModal(false)} disabled={loading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button
                    bsPrefix="custom-hamburger"
                    type="submit"
                    disabled={loading}
                    className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating...
                      </>
                    ) : (
                      'Create Organization Ticket'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Modal.Body>
        </Form>
      </Modal>

      {/* View Ticket Modal - Similar to user view but with admin actions */}
      {selectedTicket && (
        <Modal show={showTicketModal} onHide={() => setShowTicketModal(false)} size="xl" centered>
          <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
            <Modal.Title className="font-bold text-lg text-white">
              <code className="text-white bg-purple-700 px-2 py-1 rounded">{selectedTicket.ticket_number}</code>
              <span className="ms-2">{selectedTicket.subject}</span>
              {selectedTicket.is_organization_ticket && (
                <Badge bg="info" className="ms-2">Organization Ticket</Badge>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <Row>
              <Col md={8}>
                <Card className="mb-3 border-0 shadow-sm">
                  <Card.Header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white d-flex justify-content-between align-items-center border-0">
                    <h6 className="mb-0 text-white fw-bold">Ticket Details</h6>
                    <div>
                      {selectedTicket.status === 'open' && (
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={() => handleUpdateStatus(selectedTicket.id, 'in_progress')}
                          className="border-1 border-blue-600 bg-white text-blue-600 hover:from-blue-700 hover:to-cyan-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-xs px-3 py-1 me-2"
                        >
                          Mark In Progress
                        </Button>
                      )}
                      {selectedTicket.status === 'in_progress' && (
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={() => handleUpdateStatus(selectedTicket.id, 'resolved')}
                          className="border-1 border-green-600 bg-white text-green-600 hover:from-green-700 hover:to-emerald-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-xs px-3 py-1 me-2"
                        >
                          Mark Resolved
                        </Button>
                      )}
                      {selectedTicket.status === 'resolved' && (
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={() => handleUpdateStatus(selectedTicket.id, 'closed')}
                          className="border-1 border-gray-600 bg-white text-gray-600 hover:from-gray-700 hover:to-gray-800 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-xs px-3 py-1"
                        >
                          Close Ticket
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body className="bg-white">
                    <p><strong>Description:</strong></p>
                    <p className="text-muted">{selectedTicket.description}</p>
                    
                    <Row className="mt-3">
                      <Col md={6}>
                        <p><strong>Created By:</strong> {selectedTicket.creator.first_name} {selectedTicket.creator.last_name}</p>
                        <p><strong>Status:</strong> 
                          <Badge bg={statusColors[selectedTicket.status]} className="ms-2">
                            {selectedTicket.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </p>
                      </Col>
                      <Col md={6}>
                        <p><strong>Created:</strong> {formatDate(selectedTicket.createdAt)}</p>
                      </Col>
                    </Row>

                    {selectedTicket.resolved_at && (
                      <p><strong>Resolved:</strong> {formatDate(selectedTicket.resolved_at)}</p>
                    )}

                    {selectedTicket.resolution_notes && (
                      <div>
                        <p><strong>Resolution Notes:</strong></p>
                        <div className="bg-success bg-opacity-10 p-3 rounded border border-success">
                          {selectedTicket.resolution_notes}
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Messages - Similar to user view */}
                <Card className="border-0 shadow-sm mb-3">
                  <Card.Header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
                    <h6 className="mb-0 text-white fw-bold">Conversation ({selectedTicket.messages?.length || 0})</h6>
                  </Card.Header>
                  <Card.Body className="bg-white p-0">
                    <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '1rem' }}>
                      {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                        selectedTicket.messages.map((message) => (
                          <div key={message.id} className={`mb-3 ${message.sender.id === user.id ? 'text-end' : ''}`}>
                            <div className={`d-inline-block p-3 rounded-lg ${
                              message.sender.id === user.id 
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' 
                                : message.sender.role === 0 
                                  ? 'bg-red-100 text-red-900'
                                  : 'bg-gray-100 text-gray-800'
                            }`} style={{ maxWidth: '70%' }}>
                              <div className="fw-bold mb-1 d-flex align-items-center gap-2 flex-wrap">
                                {message.sender.first_name} {message.sender.last_name}
                                {message.sender.role === 0 && <Badge bg="danger" className="text-xs">Super Admin</Badge>}
                                {message.sender.role === 2 && <Badge bg="info" className="text-xs">Org Admin</Badge>}
                                {message.sender.role === 1 && <Badge bg="success" className="text-xs">User</Badge>}
                                {message.is_internal && <Badge bg="warning" text="dark" className="text-xs">Internal</Badge>}
                              </div>
                              <p className="mb-1 whitespace-pre-wrap">{message.message}</p>
                              <small className={message.sender.id === user.id || message.sender.role === 0 ? 'text-white opacity-80' : 'text-muted'}>
                                {formatDate(message.createdAt)}
                              </small>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <FaComments className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No messages yet. Start the conversation!</p>
                        </div>
                      )}
                    </div>

                    {selectedTicket.status !== 'closed' && (
                      <div className="p-3 bg-gray-50 border-top">
                        <Form onSubmit={handleSendMessage}>
                          <div className="mb-3">
                            <label className="d-flex align-items-center gap-2 text-purple-700 font-semibold text-sm">
                              <input 
                                type="checkbox" 
                                checked={messageForm.is_internal} 
                                onChange={(e) => setMessageForm(prev => ({ ...prev, is_internal: e.target.checked }))} 
                                className="form-check-input" 
                              />
                              Internal message (only visible to admins)
                            </label>
                          </div>
                          <div className="d-flex gap-2">
                            <textarea 
                              rows={3} 
                              placeholder="Type your response..." 
                              value={messageForm.message} 
                              onChange={(e) => setMessageForm(prev => ({ ...prev, message: e.target.value }))} 
                              className="flex-1 px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                              required 
                            />
                            <div className="d-flex flex-column gap-2">
                              <Button 
                                type="submit" 
                                disabled={loading || (!messageForm.message.trim() && !selectedFile)} 
                                bsPrefix="custom-hamburger"
                                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-2"
                              >
                                <FaPaperPlane className="w-5 h-5" />
                              </Button>
                              <label className="bg-gray-200 hover:bg-gray-300 p-2 rounded-lg cursor-pointer text-center transition-all">
                                <FaPaperclip className="w-5 h-5 text-gray-700" />
                                <input 
                                  type="file" 
                                  className="d-none" 
                                  onChange={handleFileSelect} 
                                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt" 
                                />
                              </label>
                            </div>
                          </div>

                          {selectedFile && (
                            <div className="mt-3 p-2 bg-purple-50 rounded-lg d-flex justify-content-between align-items-center">
                              <span className="d-flex align-items-center gap-2 text-sm">
                                <FaPaperclip /> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                              </span>
                              <button 
                                type="button" 
                                onClick={() => setSelectedFile(null)} 
                                className="text-danger btn btn-link p-0"
                              >
                                <FaTimes />
                              </button>
                            </div>
                          )}
                        </Form>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                {/* Attachments */}
                {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
                      <h6 className="mb-0 text-white fw-bold">Attachments</h6>
                    </Card.Header>
                    <Card.Body className="bg-white">
                      {selectedTicket.attachments.map((attachment) => (
                        <div key={attachment.id} className="d-flex align-items-center mb-3 p-2 bg-gray-50 rounded-lg">
                          {attachment.is_image ? (
                            <FaImage className="text-purple-600 me-2" size={20} />
                          ) : (
                            <FaFile className="text-purple-600 me-2" size={20} />
                          )}
                          <div className="flex-grow-1">
                            <div className="fw-semibold text-sm text-gray-800">{attachment.original_filename}</div>
                            <small className="text-muted text-xs">
                              {(attachment.file_size / 1024).toFixed(1)} KB
                            </small>
                          </div>
                          <Button
                            bsPrefix="custom-hamburger"
                            onClick={() => handleDownloadAttachment(attachment.id)}
                            className="border-0 border-purple-300 text-purple-600 rounded hover:bg-purple-50 transition p-1"
                            title="Download"
                          >
                            <FaDownload className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
            <Button
              bsPrefix="custom-hamburger"
              onClick={() => setShowTicketModal(false)}
              className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm px-4 py-2"
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default OrgSupportTicketsPage;
